import { useCallback, useEffect, useRef, useState } from 'react';
import { api, fetchAutenticado, ErrorApi } from '../api/cliente';
import { Error as AvisoError } from '../componentes/comunes';
import { useSesion } from '../sesion';

/**
 * Complemento de pagos: registrar lo que el cliente depositó.
 *
 * Karin: «nos falta una sección de complemento de pagos, eso nos ayuda a
 * registrar los pagos que hace el cliente». Walmart no avisa factura por
 * factura: deposita una vez y publica en su portal un reporte con todas
 * las que ese depósito cubrió. El de muestra trae 188 en un solo pago de
 * 1,256,714.17.
 *
 * ── Lo que esta pantalla automatiza ─────────────────────────────────
 *
 * Las cinco revisiones que hoy se hacen a mano sobre ese archivo, tal
 * como Karin las describió:
 *
 *   «valido las facturas que nos pagaron, confirmo los montos
 *   (principalmente reviso el total pagado vs el total factura), confirmo
 *   que todos correspondan al mismo día de pago, confirmo que todas las
 *   facturas pagadas correspondan el mismo número de documento y por
 *   último reviso que la Σ coincida con el monto total que dice el
 *   documento».
 *
 * Las tres del depósito salen arriba, en grande, porque son las que
 * deciden si el archivo se puede registrar completo. Las dos de cada
 * factura salen en la tabla, renglón por renglón.
 *
 * ── Se ve antes de aplicar ──────────────────────────────────────────
 *
 * El archivo se sube dos veces a propósito, igual que al integrar los
 * UUID: primero para ver qué pasaría y luego para aplicarlo. Dar por
 * cobrada una factura que no pagaron no se deshace con un botón.
 */

const NOMBRE_RESULTADO: Record<string, string> = {
  se_cobra: 'Se cobra',
  se_cobra_sin_uuid: 'Se cobra · sin UUID',
  no_encontrada: 'No se encontró',
  monto_distinto: 'Importe distinto',
  ya_pagada: 'Ya estaba pagada',
  cancelada: 'Cancelada',
  repetida: 'Repetida en el archivo',
};

/** Los que sí mueven dinero. El resto queda anotado y sin tocar. */
const SE_COBRAN = ['se_cobra', 'se_cobra_sin_uuid'];

interface Revision {
  bien: boolean;
  que: string;
  detalle: string;
}

interface Renglon {
  renglon: number;
  resultado: string;
  mensaje: string;
  folio: string;
  totalDelArchivo: number;
  totalDeLaFactura: number | null;
  montoAAplicar: number;
}

interface Resumen {
  total: number;
  seCobran: number;
  sinUuid: number;
  noEncontradas: number;
  montoDistinto: number;
  yaPagadas: number;
  canceladas: number;
  repetidas: number;
  importe: number;
}

interface Respuesta {
  revisiones: Revision[];
  sePuedeAplicar: boolean;
  pago: {
    fechaPago: string | null;
    documento: string | null;
    montoDelDocumento: number | null;
  };
  resumen: Resumen;
  renglones: Renglon[];
}

interface PagoRegistrado {
  id: string;
  fechaPago: string;
  referencia: string | null;
  archivoNombre: string | null;
  cliente: string | null;
  montoTotal: number;
  facturas: number;
  aplicado: number;
  serieComplemento: string | null;
  folioComplemento: number | null;
  complementoGeneradoEn: string | null;
}

function moneda(valor: number): string {
  return valor.toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });
}

function fecha(iso: string): string {
  return new Date(iso).toLocaleDateString('es-MX', {
    day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC',
  });
}

/** Sube el archivo. Va con fetch porque es multipart y no JSON. */
async function subir(archivo: File, aplicar: boolean): Promise<Respuesta> {
  const cuerpo = new FormData();
  cuerpo.append('archivo', archivo);

  const respuesta = await fetchAutenticado(
    aplicar ? '/api/v1/facturacion/cobranza' : '/api/v1/facturacion/cobranza/vista-previa',
    { method: 'POST', body: cuerpo },
  );

  const datos = await respuesta.json().catch(() => ({}));

  if (!respuesta.ok) {
    throw new ErrorApi(
      respuesta.status,
      String(datos.codigo_negocio ?? 'error'),
      String(datos.detail ?? 'No se pudo leer el archivo.'),
      Array.isArray(datos.errors) ? datos.errors : undefined,
    );
  }

  return datos as Respuesta;
}

export default function ComplementoDePagos() {
  const { puede } = useSesion();
  const entrada = useRef<HTMLInputElement>(null);
  const [archivo, setArchivo] = useState<File | null>(null);
  const [previa, setPrevia] = useState<Respuesta | null>(null);
  const [aplicado, setAplicado] = useState<Resumen | null>(null);
  const [trabajando, setTrabajando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [historial, setHistorial] = useState<PagoRegistrado[]>([]);

  const cargarHistorial = useCallback(() => {
    api.get<PagoRegistrado[]>('/facturacion/cobranza/historial')
      .then(setHistorial)
      .catch(() => undefined);
  }, []);

  useEffect(cargarHistorial, [cargarHistorial]);

  function elegir(nuevo: File | null) {
    setArchivo(nuevo);
    setPrevia(null);
    setAplicado(null);
    setError(null);
    if (!nuevo) return;

    setTrabajando(true);
    subir(nuevo, false)
      .then(setPrevia)
      .catch(setError)
      .finally(() => setTrabajando(false));
  }

  function aplicar() {
    if (!archivo) return;

    setTrabajando(true);
    setError(null);
    subir(archivo, true)
      .then((r) => {
        setAplicado(r.resumen);
        setPrevia({ ...(previa as Respuesta), ...r });
        cargarHistorial();
      })
      .catch(setError)
      .finally(() => setTrabajando(false));
  }

  const hayQueCobrar = (previa?.resumen.seCobran ?? 0) > 0;

  return (
    <>
      <div className="panel">
        <h2>Registrar el pago del cliente</h2>
        <p className="sub">
          El reporte de pagos que se descarga del portal del cliente. Cada
          archivo es un depósito y trae todas las facturas que cubrió. Se
          revisa primero y no se cobra nada hasta que lo apruebes.
        </p>

        <div
          className="aviso"
          role="button"
          tabIndex={0}
          style={{ textAlign: 'center', cursor: 'pointer', padding: 24, marginTop: 12 }}
          onClick={() => entrada.current?.click()}
          onKeyDown={(e) => { if (e.key === 'Enter') entrada.current?.click(); }}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            elegir(e.dataTransfer.files[0] ?? null);
          }}
        >
          {archivo
            ? <strong>{archivo.name}</strong>
            : <>Arrastra aquí el reporte de pagos, o haz clic para elegirlo.</>}
          {/* No se pregunta de qué cliente es: el archivo ya lo dice por
              sus encabezados. Un desplegable agregaría un clic que se
              puede equivocar, y equivocarlo significaría leer importes de
              columnas que no son — un error que no se ve, porque los
              números salen igual de plausibles. */}
          <div className="conteo" style={{ marginTop: 6 }}>
            El Excel del portal, tal como se baja. Se entienden los de
            Walmart y los de Soriana, y el sistema reconoce solo cuál es.
            Se leen la factura, el importe, la fecha de pago, el número de
            documento y el monto del depósito; lo demás se ignora.
          </div>
        </div>

        <input
          ref={entrada}
          type="file"
          accept=".xlsx,.xlsm,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          style={{ display: 'none' }}
          onChange={(e) => elegir(e.target.files?.[0] ?? null)}
        />

        <AvisoError error={error} />

        {trabajando && <p className="sub">Leyendo el archivo…</p>}

        {previa && (
          <>
            {/* Las tres revisiones del depósito, arriba y siempre.

                Se enseñan aunque pasen: quien va a dar por cobradas 188
                facturas necesita ver que las revisiones corrieron, no
                sólo que nadie se quejó. */}
            <div className="panel" style={{ marginTop: 16 }}>
              <h3 style={{ marginTop: 0 }}>El depósito</h3>
              <div className="rejilla-campos">
                <Dato
                  etiqueta="Fecha de pago"
                  valor={previa.pago.fechaPago ? fecha(previa.pago.fechaPago) : '—'}
                />
                <Dato etiqueta="Documento" valor={previa.pago.documento ?? '—'} mono />
                <Dato
                  etiqueta="Monto del documento"
                  valor={previa.pago.montoDelDocumento != null
                    ? moneda(previa.pago.montoDelDocumento) : '—'}
                />
              </div>

              <div style={{ display: 'grid', gap: 6, marginTop: 12 }}>
                {previa.revisiones.map((r) => (
                  <div
                    key={r.que}
                    style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}
                  >
                    <span style={{
                      color: r.bien ? 'var(--exito, #1a7f4b)' : '#b02a37',
                      fontWeight: 700,
                    }}>
                      {r.bien ? '✓' : '✗'}
                    </span>
                    <span style={{ fontWeight: 600 }}>{r.que}</span>
                    <span className="conteo">{r.detalle}</span>
                  </div>
                ))}
              </div>

              {!previa.sePuedeAplicar && (
                <div className="aviso error" style={{ marginTop: 12 }}>
                  <strong>Este archivo no se puede registrar como un solo pago.</strong>{' '}
                  Un depósito tiene una fecha y un monto; si el archivo trae
                  más de uno, son pagos distintos y hay que separarlos antes
                  de subirlos.
                </div>
              )}
            </div>

            <div className="rejilla-catalogos" style={{ marginTop: 16 }}>
              <Conteo titulo="Facturas en el archivo" valor={previa.resumen.total} />
              <Conteo
                titulo={aplicado ? 'Cobradas' : 'Se van a cobrar'}
                valor={previa.resumen.seCobran}
              />
              <Conteo
                titulo="Importe distinto"
                valor={previa.resumen.montoDistinto}
                alerta={previa.resumen.montoDistinto > 0}
              />
              <Conteo
                titulo="No se encontraron"
                valor={previa.resumen.noEncontradas}
                alerta={previa.resumen.noEncontradas > 0}
              />
              <Conteo titulo="Ya estaban pagadas" valor={previa.resumen.yaPagadas} />
            </div>

            <div style={{ marginTop: 12, fontSize: 18, textAlign: 'right' }}>
              Se van a aplicar <strong>{moneda(previa.resumen.importe)}</strong>
            </div>

            {previa.resumen.sinUuid > 0 && (
              <div className="aviso" style={{ marginTop: 12 }}>
                {previa.resumen.sinUuid} de las que se cobran todavía no tienen
                UUID. El dinero entró igual, pero les falta integrar el
                timbrado: una factura cobrada y sin timbrar es un problema
                que nadie va a buscar si no se dice aquí.
              </div>
            )}

            {aplicado ? (
              <div className="aviso exito" style={{ marginTop: 14 }}>
                Listo: {aplicado.seCobran} facturas quedaron cobradas por{' '}
                {moneda(aplicado.importe)}.
                {aplicado.total - aplicado.seCobran > 0 && (
                  <> Quedan {aplicado.total - aplicado.seCobran} renglones sin
                  aplicar, con su motivo abajo. Ninguno se tocó.</>
                )}
              </div>
            ) : (
              <div className="controles" style={{ marginTop: 14 }}>
                {puede('facturacion', 'crear') && (
                  <button
                    className="primario"
                    onClick={aplicar}
                    disabled={trabajando || !hayQueCobrar || !previa.sePuedeAplicar}
                  >
                    Registrar el pago de {moneda(previa.resumen.importe)}
                  </button>
                )}
                <button onClick={() => elegir(null)} disabled={trabajando}>Descartar</button>
                {!hayQueCobrar && (
                  <span className="conteo">
                    No hay ninguna factura que cobrar en este archivo.
                  </span>
                )}
              </div>
            )}

            <table style={{ marginTop: 16 }}>
              <thead>
                <tr>
                  <th className="numero">Renglón</th>
                  <th>Factura</th>
                  <th className="numero">Dice el archivo</th>
                  <th className="numero">Dice la factura</th>
                  <th className="numero">Se aplica</th>
                  <th>Resultado</th>
                  <th>Qué pasa</th>
                </tr>
              </thead>
              <tbody>
                {previa.renglones.map((r) => (
                  <tr key={r.renglon}>
                    <td className="numero">{r.renglon}</td>
                    <td className="mono">{r.folio}</td>
                    <td className="numero">{moneda(r.totalDelArchivo)}</td>
                    <td className="numero">
                      {r.totalDeLaFactura != null ? moneda(r.totalDeLaFactura) : '—'}
                    </td>
                    <td className="numero">
                      {r.montoAAplicar > 0 ? moneda(r.montoAAplicar) : '—'}
                    </td>
                    <td>
                      <span className={SE_COBRAN.includes(r.resultado)
                        ? 'etiqueta si'
                        : r.resultado === 'ya_pagada' ? undefined : 'etiqueta no'}
                      >
                        {NOMBRE_RESULTADO[r.resultado] ?? r.resultado}
                      </span>
                    </td>
                    <td className="conteo">{r.mensaje}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>

      {historial.length > 0 && (
        <div className="panel">
          <h2>Pagos registrados</h2>
          <p className="sub">
            Cuando una factura aparece cobrada y nadie recuerda con qué
            depósito, esto lo contesta.
          </p>
          <table>
            <thead>
              <tr>
                <th>Fecha de pago</th>
                <th>Documento</th>
                <th>Cliente</th>
                <th className="numero">Monto del depósito</th>
                <th className="numero">Facturas</th>
                <th className="numero">Aplicado</th>
                <th>Complemento</th>
                <th>Archivo</th>
              </tr>
            </thead>
            <tbody>
              {historial.map((p) => (
                <tr key={p.id}>
                  <td>{fecha(p.fechaPago)}</td>
                  <td className="mono">{p.referencia ?? '—'}</td>
                  <td>{p.cliente ?? '—'}</td>
                  <td className="numero">{moneda(p.montoTotal)}</td>
                  <td className="numero">{p.facturas}</td>
                  <td className="numero">
                    {moneda(p.aplicado)}
                    {/* Aplicado menos que el depósito significa que alguna
                        factura del archivo no se pudo cobrar. No es un
                        error: es dinero que entró y todavía no tiene a qué
                        factura ir. */}
                    {p.aplicado < p.montoTotal && (
                      <div className="conteo" style={{ color: '#b02a37' }}>
                        faltan {moneda(p.montoTotal - p.aplicado)}
                      </div>
                    )}
                  </td>
                  <td>
                    {/* El folio se aparta la primera vez y ya no se
                        suelta: un consecutivo fiscal no se devuelve. Por
                        eso la segunda descarga dice el mismo número en
                        vez de sacar uno nuevo. */}
                    {p.folioComplemento != null && (
                      <div className="mono">
                        {p.serieComplemento}-{p.folioComplemento}
                      </div>
                    )}
                    <button
                      className="enlace"
                      onClick={() => {
                        void api.descargar(
                          `/facturacion/cobranza/historial/${p.id}/complemento`,
                        ).then(cargarHistorial).catch(setError);
                      }}
                    >
                      {p.complementoGeneradoEn ? 'Descargar otra vez' : 'Generar complemento'}
                    </button>
                    {p.complementoGeneradoEn && (
                      <div
                        className="conteo"
                        style={{ color: 'var(--exito, #1a7f4b)', fontWeight: 600 }}
                      >
                        ✓ generado {fecha(p.complementoGeneradoEn)}
                      </div>
                    )}
                  </td>
                  <td className="mono conteo">{p.archivoNombre ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function Conteo({ titulo, valor, alerta }: { titulo: string; valor: number; alerta?: boolean }) {
  return (
    <div className="tarjeta" style={{ cursor: 'default' }}>
      <div className="conteo">{titulo}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: alerta ? '#b02a37' : undefined }}>
        {valor}
      </div>
    </div>
  );
}

function Dato({ etiqueta, valor, mono }: { etiqueta: string; valor: string; mono?: boolean }) {
  return (
    <div className="campo">
      <label>{etiqueta}</label>
      <div className={mono ? 'mono' : undefined} style={{ fontWeight: 600 }}>{valor}</div>
    </div>
  );
}
